"use client"
import { useEffect, useState, useMemo, useCallback } from "react"
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom"
import { getUser, logout, getAuthHeader, getToken } from "./utils/auth";
import {
  BOOKING_MANAGER_ROLE,
  BOOKING_SETTINGS_PATH,
  MENU_GROUPS,
  hasPermission,
  isOemRestrictedPath
} from "./constants/menuDefs";
import ProfileCompletionModal from "./components/ProfileCompletionModal";
import { Loader2 } from 'lucide-react';
import {
  getEnglishBetaGroupLabel,
  getEnglishBetaMenuLabel,
  isEnglishBetaVisiblePath,
  isEnglishOperatorMode,
} from "./utils/operatorMode";
import { canViewApiKeysSurface, canViewOemApiKeysSurface } from "./utils/apiSurfaceAccess";
import UiSurfaceSelector from "./uiux-runtime/UiSurfaceSelector";
import { UIUX_THREE_TENANT_PILOT_RELEASE_ID } from "./uiux-runtime/uiuxPilotRelease";

// --- Constants ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;
// 2026-08-18: role='admin'（本家管理者）も新UIへ切替可能に（オーナー要望）。
const UIUX_SELECTOR_ROLES = new Set(["client_admin", "staff", "admin"]);

// --- SVG Icons ---
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
)

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const ChevronUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
)

// --- Components ---

function JobProgressItem({ job, onCancel }) {
  const [isCancelling, setIsCancelling] = useState(false);
  const progressPercentage = job.total > 0 ? (job.progress / job.total) * 100 : 0;

  let statusText = "処理を開始しています...";
  let bgColor = "bg-blue-600";
  const isCancellable = job.status === "pending" || job.status === "processing";

  switch (job.status) {
    case "pending": statusText = `待機中... (Job ID: ${job.id})`; break;
    case "processing": statusText = `処理中: ${job.progress || 0} / ${job.total || 0} 件`; break;
    case "completed": statusText = `完了: ${job.result_message || "ジョブ完了"}`; bgColor = "bg-green-600"; break;
    case "failed": statusText = `失敗: ${job.result_message || "エラー発生"}`; bgColor = "bg-red-600"; break;
    default: break;
  }

  const handleCancelClick = async () => {
    if (!window.confirm(`ジョブID: ${job.id} の送信処理を本当にキャンセルしますか？`)) return;
    setIsCancelling(true);
    await onCancel(job.id);
    setIsCancelling(false);
  };

  return (
    <div className="p-3 bg-gray-50 rounded-md border">
      <p className="text-sm text-gray-600 mb-2 break-words">{statusText}</p>
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
        <div className={`${bgColor} h-2.5 rounded-full transition-all duration-500`} style={{ width: `${progressPercentage}%` }}></div>
      </div>
      {isCancellable && (
        <div className="text-right">
          <button onClick={handleCancelClick} disabled={isCancelling} className="bg-red-500 text-white text-xs font-bold py-1 px-2 rounded-md hover:bg-red-600 disabled:bg-gray-400">
            {isCancelling ? "キャンセル中..." : "キャンセル"}
          </button>
        </div>
      )}
    </div>
  );
}

export function GlobalJobNotifier({ jobs, onCancel, onClearFinished, latestLog, className = "" }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing' || j.status === 'starting');
  const finishedJobs = jobs.filter(j => j.status === 'completed' || j.status === 'failed');

  if (jobs.length === 0) return null;

  return (
    <div className={`fixed bottom-3 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-80 bg-white rounded-lg shadow-xl z-50 border-t-4 border-blue-500 animate-fade-in-up ${className}`.trim()}>
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex justify-between items-center p-3 sm:p-4">
        <h4 className="text-sm sm:text-md font-bold text-gray-800">
          {activeJobs.length > 0 ? `${activeJobs.length}件のジョブを実行中` : 'ジョブ結果'}
        </h4>
        <svg className={`w-5 h-5 transform transition-transform text-gray-500 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path></svg>
      </button>
      
      {isExpanded && (
        <>
          {latestLog && (
            <div className="px-4 pb-2 border-b">
              <p className="text-xs text-gray-500 font-mono truncate">{latestLog}</p>
            </div>
          )}
          <div className="p-3 sm:p-4 pt-2 max-h-64 sm:max-h-80 overflow-y-auto">
            <div className="space-y-3">
              {activeJobs.map(job => <JobProgressItem key={job.id} job={job} onCancel={onCancel} />)}
              {finishedJobs.length > 0 && (
                <>
                  {activeJobs.length > 0 && <hr className="my-3"/>}
                  {finishedJobs.map(job => <JobProgressItem key={job.id} job={job} onCancel={() => {}} />)}
                  <button onClick={onClearFinished} className="w-full text-center text-xs text-gray-500 hover:text-gray-800 py-1 mt-2">完了した通知をクリア</button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SystemStatusMonitor({ activeJobCount, isMaintenanceMode, serverStatus }) {
  const isSafe = activeJobCount === 0
  const hasServerStatus = Boolean(serverStatus)
  const usedProcesses = Number(serverStatus?.processingJobCount ?? 0)
  const maxProcesses = Number(serverStatus?.maxConcurrentJobs ?? 0)
  const congestionRate = Number(serverStatus?.congestionRate ?? 0)
  const pendingJobCount = Number(serverStatus?.pendingJobCount ?? 0)

  let statusText = `現在 ${activeJobCount}件 のジョブが稼働中...`
  let bgColor = "bg-amber-100"
  let textColor = "text-amber-800"

  if (isSafe) {
    statusText = "稼働中のジョブはありません。"
    bgColor = "bg-green-100"
    textColor = "text-green-800"
  }

  if (isMaintenanceMode) {
    statusText = isSafe ? "安全に再起動できます。" : `完了待ち: ${activeJobCount}件`
    if (isSafe) bgColor = "bg-sky-100"
    if (isSafe) textColor = "text-sky-800"
  }

  return (
    <div className={`p-3 rounded-md ${bgColor} ${textColor}`}>
      <p className="font-bold text-sm">システム稼働状況</p>
      <p className="text-xs mt-1">{statusText}</p>
      <div className="mt-2 space-y-0.5 text-xs">
        {hasServerStatus ? (
          <>
            <p>
              プロセス使用状況: <span className="font-bold">{usedProcesses}</span> / {maxProcesses}枠
            </p>
            <p>混雑率: {congestionRate}% / 待機中: {pendingJobCount}件</p>
          </>
        ) : (
          <p>プロセス使用状況: 取得中...</p>
        )}
      </div>
    </div>
  )
}


function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [clients, setClients] = useState([])
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeJobs, setActiveJobs] = useState([]);
  const [latestLog, setLatestLog] = useState("");
  const [activeJobCount, setActiveJobCount] = useState(0)
  const [serverStatus, setServerStatus] = useState(null)
  const [isSafeToRestart, setIsSafeToRestart] = useState(false)
  
  const [user, setUser] = useState(getUser);
  const isBookingManager = user?.role === BOOKING_MANAGER_ROLE;

  // 2026-08-20: 新UIの運営パネルが管理画面を iframe で開くときだけ、
  // 旧UIのシェル(サイドバー・企業選択・モバイルヘッダー・ジョブ通知・
  // 運営用の5秒ポーリング)を描かない。これが無いと、新UIの中に
  // 旧UIのサイドバーと企業選択がもう一枚出て二重になる
  // (管理ルートは全部 App.js:752 の AppLayout の中にあるため)。
  //
  // クエリだけを条件にすると一般テナントが URL を書き換えて
  // サイドバー無しの画面に入れてしまうので、運営ロールとの二重条件にする。
  const isEmbeddedAdminView = new URLSearchParams(location.search).get("embedded") === "1"
    && (user?.role === "admin" || user?.role === "admin_staff");
  const [loading, setLoading] = useState(true);
  const [privileges, setPrivileges] = useState(null);

  const [isClientSelectOpen, setIsClientSelectOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState("");

  const [oemBranding, setOemBranding] = useState(null);

  useEffect(() => {
    const fetchBranding = async () => {
      if (!user?.oem_id) {
        document.title = "SalesBreaker";
        const link = document.querySelector("link[rel~='icon']");
        if (link) link.href = `${window.location.origin}/app/favicon.ico`;
        return;
      }

      // ログイン済みかつOEMユーザーの場合、またはドメインによる自動判別を行う準備
      try {
        const brandingUrl = isBookingManager
          ? `${API_URL}/api/public/oem/branding-by-domain?hostname=${encodeURIComponent(window.location.hostname)}`
          : `${API_URL}/api/oem/my-branding/${user.oem_id}`;
        const res = await fetch(brandingUrl, isBookingManager ? undefined : { headers: getAuthHeader() });
        const data = await res.json();

        if (data.is_oem) {
          setOemBranding(data.branding);

          // --- 🏢 【新設】ブラウザタブの動的書き換え ---
          // 1. ページタイトルの変更
          if (data.branding.display_name) {
            document.title = data.branding.display_name;
          }

          // 2. ファビコン（アイコン）の変更
          if (data.branding.logo_path) {
            // 本家ドメインを隠すため現在のオリジンを使用
            const iconUrl = `${window.location.origin}/api/oem${data.branding.logo_path}`;
            let link = document.querySelector("link[rel~='icon']");
            
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = iconUrl;
          }
          // ------------------------------------------
        }
      } catch (e) { console.error("Branding load failed", e); }
    };
    fetchBranding();
  }, [isBookingManager, user?.oem_id, user?.id]);

  const needsProfile = useMemo(() => {
      // 1. そもそも表示対象外のロールなら false
      if (!user || user.role === 'admin' || user.role === 'admin_staff' || user.role === BOOKING_MANAGER_ROLE) return false;

      // 🚀 2. 会社として電話番号が登録済みなら、個人が空でも false (壁を出さない)
      if (privileges?.clientPhone) return false;
      
      // 3. 会社も個人も未登録なら true (壁を出す)
      return !user.company || !user.phone;
  }, [user, privileges]); // 🚀 依存配列に privileges を追加

  // ★ 追加：プロフィール更新成功時の処理
  const handleProfileUpdated = (updatedUser) => {
    // localStorageを更新し、stateも更新してモーダルを消す
    localStorage.setItem("userInfo", JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const [userPrivileges, setUserPrivileges] = useState(null);
  // 未払い請求の「いますぐ支払う」リンク (2026-08-19)。決済に失敗した
  // お客さんが自力で払い直せる唯一の導線。Stripe側でURLが失効しうるので
  // 保存はせず、支払い遅延を検知したときに都度取りに行く。
  const [openInvoice, setOpenInvoice] = useState(null);

  // メニューの開閉状態管理（キーはグループ名）
  const [openGroups, setOpenGroups] = useState({});

  const selectedClientId = useMemo(() => {
    if (!user) return null
    return user.role === "admin" || user.role === "admin_staff"
      ? localStorage.getItem("selectedClientId") || "all"
      : user.client_id
  }, [user])

  const [metaStatus, setMetaStatus] = useState({ fb_page_id: null, daysRemaining: null });

  useEffect(() => {
    // 連携していない、あるいは全クライアント表示時はチェックしない
    if (isBookingManager || !selectedClientId || selectedClientId === "all") {
      setMetaStatus({ fb_page_id: null, daysRemaining: null });
      return;
    }

    const fetchMetaStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/client-info?client_id=${selectedClientId}`, { 
          headers: getAuthHeader() 
        });
        const data = await res.json();
        if (data.success && data.info) {
          const expiry = data.info.fb_token_expires_at;
          // 残り日数を計算
          const days = expiry ? Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;
          setMetaStatus({ 
            fb_page_id: data.info.fb_page_id, 
            daysRemaining: days 
          });
        }
      } catch (e) { 
        console.error("Metaステータス取得エラー:", e); 
      }
    };

    fetchMetaStatus();
  }, [isBookingManager, selectedClientId]);

  useEffect(() => {
    if (user?.role === BOOKING_MANAGER_ROLE && location.pathname !== BOOKING_SETTINGS_PATH) {
      navigate(BOOKING_SETTINGS_PATH, { replace: true });
      return;
    }

    // 🚀 2. OEMユーザーであり、かつ現在のURLが禁止リストに含まれている場合
    if (user?.oem_id && isOemRestrictedPath(location.pathname)) {
      console.warn(`[GUARD] OEM user tried to access restricted area: ${location.pathname}`);
      // 🚀 3. 強制的にマイページへリダイレクト（replace: true で履歴に残さない）
      navigate("/mypage", { replace: true });
    }

    // 既存の処理（ページ遷移時にモバイルメニューを閉じる）
    setIsMenuOpen(false);
  }, [location.pathname, user?.oem_id, user?.role, navigate]); // 🚀 監視対象に oem_id と navigate を追加

  const handleLinkClick = () => {
    if (window.innerWidth < 768) {
      setIsMenuOpen(false)
    }
  }

  const addActiveJob = useCallback((newJob) => {
    if (newJob && newJob.id) {
      setActiveJobs(prevJobs => {
        const jobExists = prevJobs.some(job => job.id === newJob.id);
        if (jobExists) {
          return prevJobs.map(job => job.id === newJob.id ? { ...job, ...newJob } : job);
        }
        return [...prevJobs, { progress: 0, total: '...', result_message: '', ...newJob }];
      });
    } else {
      console.error("addActiveJobに無効なjobオブジェクトが渡されました。", newJob);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.role) {
      navigate("/login")
    }
  }, [user, navigate])

  useEffect(() => {
    if (user && user.id) {
        if (user.role === BOOKING_MANAGER_ROLE) {
            setUserPrivileges({ role: BOOKING_MANAGER_ROLE });
            return;
        }
        const fetchUserPrivileges = async () => {
            try {
                const res = await fetch(`${API_URL}/api/user-privileges?userId=${user.id}`, {
                    headers: getAuthHeader()
                });
                const data = await res.json();
                if (data.success) {
                  setUserPrivileges(data.privileges);
                  
                  // 🚀 修正：最新の権限情報（oem_id 含む）でユーザー状態を上書きする
                  setUser(prev => ({
                      ...prev,
                      oem_id: data.privileges.oem_id, // これで hasPermission が OEM だと認識できる
                      permissions: data.privileges.permissions
                  }));
                } else {
                    console.error("権限情報の取得に失敗:", data.error);
                    setUserPrivileges({ role: user.role }); 
                }
            } catch (err) {
                console.error("権限情報APIの通信に失敗しました:", err);
                setUserPrivileges({ role: user.role });
            }
        };
        fetchUserPrivileges();
    }
  }, [user?.id, user?.role]);

  // 支払いが滞っているときだけ、その場で払い直せるリンクを取りに行く。
  // 正常なテナントでは1リクエストも増やさない。
  useEffect(() => {
    const status = userPrivileges?.subscription_status;
    if (status !== 'past_due' && status !== 'incomplete') {
      setOpenInvoice(null);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/billing/open-invoice`, { headers: getAuthHeader() });
        if (!res.ok) return;
        const data = await res.json();
        if (active && data?.success) setOpenInvoice(data.invoice || null);
      } catch (_err) {
        // 取れなくてもバナー自体は出す (設定画面への導線は従来どおり残る)。
      }
    })();
    return () => { active = false; };
  }, [userPrivileges?.subscription_status]);

  // ログストリーム等のEffect...
  useEffect(() => {
    if (isBookingManager || isEmbeddedAdminView || !selectedClientId) return;
    const token = getToken();
    const eventSource = new EventSource(`${API_URL}/api/log-stream?clientId=${selectedClientId}&token=${token}`);
    eventSource.onopen = () => console.log("ライブログストリームに接続しました。");
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.jobId && data.message) {
          setLatestLog(data.message);
        }
      } catch (e) {
        setLatestLog(event.data);
      }
    };
    eventSource.onerror = (err) => {
      console.error("EventSource failed:", err);
      eventSource.close();
    };
    return () => {
      eventSource.close();
    };
  }, [isBookingManager, selectedClientId]);

  // Active Jobs Polling...
  useEffect(() => {
    if (isBookingManager || isEmbeddedAdminView || !selectedClientId) return;
    const fetchAllActiveJobs = async () => {
        try {
            const res = await fetch(`${API_URL}/api/jobs/active?client_id=${selectedClientId}`, {
                headers: getAuthHeader()
            });
            const result = await res.json();
            if (result.success) {
                setActiveJobs(prevJobs => {
                    const finishedJobs = prevJobs.filter(j => j.status === 'completed' || j.status === 'failed');
                    const newActiveJobs = result.jobs || [];
                    const stillRelevantFinishedJobs = finishedJobs.filter(fj => 
                        !newActiveJobs.some(aj => aj.id === fj.id)
                    );
                    return [...newActiveJobs, ...stillRelevantFinishedJobs];
                });
            }
        } catch (error) {
            console.error("Active jobs polling failed:", error);
        }
    };
    fetchAllActiveJobs();
    const intervalId = setInterval(fetchAllActiveJobs, 5000);
    return () => clearInterval(intervalId);
  }, [isBookingManager, selectedClientId]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "admin_staff") {
      // 🚀 修正: 自分の oem_id をパラメータに付与してリクエスト
      const url = user.oem_id 
        ? `${API_URL}/api/clients?oem_id=${user.oem_id}` 
        : `${API_URL}/api/clients`;

      fetch(url, { headers: getAuthHeader() })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then((data) => {
          if (data.success) setClients(data.clients || [])
        })
        .catch((err) => console.error("クライアント一覧取得エラー:", err))
    }
  }, [user])

  useEffect(() => {
    if (isBookingManager) return;

    if (user?.role !== "admin") {
      const checkMaintenance = () => {
        fetch(`${API_URL}/api/maintenance/status`, { headers: getAuthHeader() })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) setIsMaintenanceMode(data.maintenanceMode)
          })
          .catch((err) => console.warn("メンテナンス状態の取得をスキップ:", err))
      }
      checkMaintenance()
      const intervalId = setInterval(checkMaintenance, 60000)
      return () => clearInterval(intervalId)
    }

    const fetchAdminStatus = () => {
      fetch(`${API_URL}/api/maintenance/status`, { headers: getAuthHeader() })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) setIsMaintenanceMode(data.maintenanceMode)
        })
        .catch((err) => console.warn("メンテナンス状態の取得に失敗:", err))
      fetch(`${API_URL}/api/jobs/active-status`, { headers: getAuthHeader() })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setActiveJobCount(data.activeJobCount)
            setIsSafeToRestart(data.isSafeToRestart)
          }
        })
        .catch((err) => console.warn("稼働中ジョブ状態の取得に失敗:", err))
      fetch(`${API_URL}/api/server-status`, { headers: getAuthHeader() })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) setServerStatus(data)
        })
        .catch((err) => console.warn("サーバー利用状況の取得に失敗:", err))
    }
    // 埋め込み表示のときは親(新UI)が同じ情報を持っているので回さない。
    if (isEmbeddedAdminView) return
    fetchAdminStatus()
    const intervalId = setInterval(fetchAdminStatus, 5000)
    return () => clearInterval(intervalId)
  }, [isBookingManager, isEmbeddedAdminView, user])

  const handleLogout = useCallback(() => {
    const oemDomain = oemBranding?.domain; // 取得したドメイン
    logout();
    
    if (oemDomain) {
      // OEMドメインがあれば、そちらのログイン画面へジャンプ
      window.location.href = `https://${oemDomain}/app/login`;
    } else {
      // 本家ユーザーは通常通り navigate
      navigate("/login");
    }
  }, [navigate, setUser, oemBranding]);

  const handleCancelJob = useCallback(
    async (jobId) => {
      try {
        const res = await fetch(`${API_URL}/api/jobs/cancel`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify({ jobId: jobId }),
        })
        const data = await res.json()
        if (!res.ok || !data.success) {
          throw new Error(data.error || "キャンセルの受付に失敗しました。")
        }
        alert("ジョブのキャンセルを受け付けました。")
        setActiveJobs(prev => prev.filter(job => job.id !== jobId));
      } catch (err) {
        console.error("ジョブキャンセルエラー:", err)
        alert(`エラー: ${err.message}`)
      }
    },
    []
  )
  
  const handleClearFinishedJobs = useCallback(() => {
    setActiveJobs(prev => prev.filter(job => job.status === 'pending' || job.status === 'processing'));
  }, []);

  const handleMaintenanceToggle = useCallback(async () => {
    const isStarting = !isMaintenanceMode
    if (isStarting) {
      if (!window.confirm(`本当にメンテナンスモードを開始しますか？\n(新しいジョブの受付が停止されます)`)) return
    } else {
      if (!isSafeToRestart) {
        alert(`まだ${activeJobCount}件のジョブが稼働中です。\n安全にメンテナンスを終了できません。`)
        return
      }
      if (!window.confirm(`稼働中のジョブはありません。メンテナンスモードを終了しますか？`)) return
    }
    const endpoint = isStarting ? "/api/maintenance/start" : "/api/maintenance/end"
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { 
        method: "POST",
        headers: getAuthHeader() })
      const data = await res.json()
      if (data.success) {
        alert(data.message)
        setIsMaintenanceMode((prev) => !prev)
      } else {
        alert(`操作失敗: ${data.error}`)
      }
    } catch (err) {
      alert(`通信エラー: ${err.message}`)
    }
  }, [isMaintenanceMode, isSafeToRestart, activeJobCount])


  // 管理者専用メニュー (変更なし)
  const adminOnlyItems = useMemo(
    () => [
      { label: "🧠 フォーム解析レビュー", path: "/admin/review-queue", roles: ["admin", "client_admin", "admin_staff"] },
      { label: "🚀 解析ルール改善", path: "/admin/learning-queue", roles: ["admin", "admin_staff"] }, 
      { label: "🤝 顧客契約管理(CRM)", path: "/client-management", roles: ["admin", "admin_staff"] },
      { label: "📞 問い合わせ管理", path: "/inquiry-management", roles: ["admin", "admin_staff"] },
      { label: "⏳ 未契約ユーザー", path: "/pending-users", roles: ["admin", "admin_staff"] },
      { label: "📢 お知らせ管理", path: "/admin/announcements", roles: ["admin"] },
      { label: "🗂 営業リスト", path: "/list", roles: ["admin"] },
      { label: "🖥️ VPS稼働状況", path: "/admin/vps-status", roles: ["admin"] },
      { label: "🤖 VPSプール管理", path: "/admin/vps-management", roles: ["admin"] },
    ],
    []
  )

  // ★★★ 修正: 権限に基づいてメニューをフィルタリング ★★★
  const isEnglishBeta = isEnglishOperatorMode() && !user?.oem_id;

  const visibleGroups = useMemo(() => {
    if (!user) return [];
    const canViewApiKeys = canViewApiKeysSurface(user);
    const canViewOemApiKeys = canViewOemApiKeysSurface(user);
    
    return MENU_GROUPS.map(group => {
      if (group.visibility === "api_keys_surface" && !canViewApiKeys) return null;
      if (group.visibility === "oem_api_keys_surface" && !canViewOemApiKeys) return null;

      // --- シングルメニューの場合 ---
      if (group.type === "single") {
        // 1. ロールチェック
        if (!group.roles.includes(user.role)) return null;
        
        // 2. 権限チェック (全ユーザー共通で hasPermission を実行するよう修正)
        // staffだけでなく、OEMの制限もここで適用されます
        if (!hasPermission(user, group.path)) return null;

        if (isEnglishBeta && !isEnglishBetaVisiblePath(group.path)) return null;
        
        return isEnglishBeta
          ? { ...group, title: getEnglishBetaMenuLabel(group.path, group.title) }
          : group;

      } else {
        // --- グループメニューの場合 ---
        const visibleItems = group.items.filter(item => {
            const isApiKeysSurfaceItem = item.visibility === "api_keys_surface";
            const isOemApiKeysSurfaceItem = item.visibility === "oem_api_keys_surface";
            if (isApiKeysSurfaceItem && !canViewApiKeys) return false;
            if (isOemApiKeysSurfaceItem && !canViewOemApiKeys) return false;

            // 1. ロールチェック
            if (!item.roles.includes(user.role)) return false;
            
            // 2. 権限チェック (全ユーザー共通で hasPermission を実行するよう修正)
            if (!isApiKeysSurfaceItem && !isOemApiKeysSurfaceItem && !hasPermission(user, item.path)) return false;

            const englishBetaPath = item.path === "/api-keys" ? "/api-keys-en" : item.path;
            if (isEnglishBeta && !isEnglishBetaVisiblePath(englishBetaPath)) return false;
            
            return true;
        }).map(item => {
          if (!isEnglishBeta) return item;
          const englishBetaPath = item.path === "/api-keys" ? "/api-keys-en" : item.path;
          return { ...item, path: englishBetaPath, label: getEnglishBetaMenuLabel(englishBetaPath, item.label) };
        });

        // 表示できるアイテムが1つもなくなったグループは非表示
        if (visibleItems.length === 0) return null;
        
        return {
          ...group,
          title: isEnglishBeta ? getEnglishBetaGroupLabel(group, group.title) : group.title,
          items: visibleItems
        };
      }
    }).filter(Boolean);
  }, [user, isEnglishBeta]);

  const visibleAdminItems = useMemo(() => {
    if (!user || !userPrivileges) return []
    if (isEnglishBeta) return []
    
    return adminOnlyItems.filter((item) => {
      const userRole = user.role;
      if (!item.roles.includes(userRole)) return false;

      // 🚀 修正: oem_id がある管理者の場合、システム管理系のメニューを非表示にする
      // (SuperAdminのみがアクセスできるべき項目をここでガード)
      const systemAdminOnlyPaths = [
        "/admin/review-queue", "/admin/learning-queue", "/client-management", 
        "/inquiry-management", "/pending-users", "/admin/announcements", 
        "/list", "/admin/api-keys", "/admin/vps-status", 
        "/admin/vps-management"
      ];
      if (user.oem_id && systemAdminOnlyPaths.includes(item.path)) return false;
      
      if (item.path === "/admin/review-queue" && userRole === "client_admin") {
        return userPrivileges.canReviewAi === true;
      }
      return true;
    });
  }, [adminOnlyItems, user, userPrivileges, isEnglishBeta]);


  // 初期ロード時やURL変更時に、現在のパスを含むグループを自動で開く
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenGroups = { ...openGroups };
    let hasChange = false;

    // Adminメニューの自動オープン
    if (currentPath.startsWith("/admin") || visibleAdminItems.some(item => item.path === currentPath)) {
        if (!newOpenGroups["admin_section"]) {
            newOpenGroups["admin_section"] = true;
            hasChange = true;
        }
    }

    // 通常メニューの自動オープン
    visibleGroups.forEach(group => {
      if (group.type === "group") {
        const hasActiveItem = group.items.some(item => item.path === currentPath);
        if (hasActiveItem && !newOpenGroups[group.id]) {
          newOpenGroups[group.id] = true;
          hasChange = true;
        }
      }
    });

    if (hasChange) {
      setOpenGroups(newOpenGroups);
    }
  }, [location.pathname, visibleGroups, visibleAdminItems]);


  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  if (!user || !user.role) {
    return null
  }

 // 🏢 【修正版】テキスト主体のロゴ表示コンポーネント
  const BrandingLogo = ({ className }) => {
    if (oemBranding) {
      // ユーザーの希望により、UI上は常に「テキスト（サービス名）」を表示
      // 色は代理店設定のブランドカラーを適用
      return (
        <h2 className={`text-xl font-bold ${className}`} style={{ color: oemBranding.brand_color }}>
          {oemBranding.display_name}
        </h2>
      );
    }
    // 通常ユーザーは本家の青色ロゴテキスト
    const fallbackName = user?.oem_id ? "Partner Portal" : "Sales Breaker";
    return <h2 className={`text-xl font-bold text-blue-600 ${className}`}>{fallbackName}</h2>;
  };

  const clientContextSelector = (user.role === "admin" || user.role === "admin_staff") ? (
    <div className="p-4 border-b border-gray-200 bg-gray-50/50">
      <div className="relative">
        <label className="text-xs font-medium text-gray-600 block mb-2">
          {isEnglishBeta ? "Client context" : "クライアント選択"}
        </label>

        {/* カスタムドロップダウントリガー */}
        <button
          onClick={() => setIsClientSelectOpen(!isClientSelectOpen)}
          className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm shadow-sm flex justify-between items-center focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all hover:bg-gray-50"
        >
          <span className="truncate pr-2 font-bold text-gray-700">
            {selectedClientId === "all"
              ? (isEnglishBeta ? "All clients" : "全てのクライアント")
              : clients.find(c => c.client_id === selectedClientId)?.name || selectedClientId || (isEnglishBeta ? "Not selected" : "未選択")}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isClientSelectOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* ドロップダウンメニュー本体 */}
        {isClientSelectOpen && (
          <>
            {/* 背景クリックで閉じる用の見えないレイヤー */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                  setIsClientSelectOpen(false);
                  setClientSearchTerm(""); // 閉じる時に検索文字をリセット
              }}
            />

            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">

              {/* 検索窓 */}
              <div className="p-2 border-b border-gray-100 bg-gray-50 relative z-50">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                     <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                  </div>
                  <input
                    type="text"
                    autoFocus
                    placeholder={isEnglishBeta ? "Search client..." : "会社名で検索..."}
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-xs focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* クライアントリスト */}
              <ul className="max-h-60 overflow-y-auto py-1 relative z-50 custom-scrollbar">
                {/* 「すべて」の選択肢は検索文字がない時か、「すべて」で検索した時だけ出す */}
                {(!clientSearchTerm || (isEnglishBeta ? "All clients" : "全てのクライアント").includes(clientSearchTerm)) && (
                    <li
                      className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${selectedClientId === "all" ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => {
                        localStorage.setItem("selectedClientId", "all");
                        setIsClientSelectOpen(false);
                        setClientSearchTerm("");
                        window.location.reload();
                      }}
                    >
                      {isEnglishBeta ? "All clients" : "全てのクライアント"}
                    </li>
                )}

                {/* フィルタリングされたクライアント一覧 */}
                {clients
                  .filter(c => {
                      if (!clientSearchTerm) return true; // 検索文字がなければ全て表示
                      const searchLower = clientSearchTerm.toLowerCase();
                      const nameLower = (c.name || "").toLowerCase();
                      const idLower = (c.client_id || "").toLowerCase();
                      return nameLower.includes(searchLower) || idLower.includes(searchLower);
                  })
                  .map((c) => (
                    <li
                      key={c.id}
                      className={`px-4 py-2.5 text-sm cursor-pointer truncate transition-colors border-t border-gray-50/50 ${c.client_id === selectedClientId ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => {
                        localStorage.setItem("selectedClientId", c.client_id);
                        setIsClientSelectOpen(false);
                        setClientSearchTerm("");
                        window.location.reload();
                      }}
                    >
                      <div className="flex flex-col">
                          <span>{c.name || "名称未設定"}</span>
                          <span className="text-[9px] text-gray-400 font-mono mt-0.5">{c.client_id.substring(0, 8)}...</span>
                      </div>
                    </li>
                  ))
                }

                {/* 検索結果が0件の場合 */}
                {clientSearchTerm && clients.filter(c => (c.name || "").toLowerCase().includes(clientSearchTerm.toLowerCase()) || (c.client_id || "").toLowerCase().includes(clientSearchTerm.toLowerCase())).length === 0 && (
                    <li className="px-4 py-4 text-xs text-center text-gray-400 font-bold">
                        {isEnglishBeta ? "No matching client" : "一致する企業が見つかりません"}
                    </li>
                )}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="flex h-screen min-h-screen font-sans bg-gray-50 md:overflow-hidden">
      {/* ★ 追加：プロフィールが不足している場合は最優先でモーダルを表示 */}
      {needsProfile && (
        <ProfileCompletionModal 
          user={user} 
          onSuccess={handleProfileUpdated} 
          isEnglishBeta={isEnglishBeta}
        />
      )}
      {/* 埋め込み表示では旧UIのサイドバーと企業選択を出さない
          (新UI側に同じものがあり、二重になるため) */}
      {!isEmbeddedAdminView && (
      <div
        className={`
          w-[82vw] max-w-xs md:w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col
          fixed md:static inset-y-0 left-0 z-30
          transform ${isMenuOpen ? "translate-x-0" : "-translate-x-full"} md:transform-none
          transition-transform duration-300 ease-in-out
          overflow-y-auto
        `}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <BrandingLogo />
          <button onClick={() => setIsMenuOpen(false)} className="md:hidden text-gray-500 hover:text-gray-700">
            <CloseIcon />
          </button>
        </div>

        {clientContextSelector}

        <nav className="flex-grow p-3 sm:p-4 space-y-1">
          
          {visibleGroups.map((group, index) => {
            if (group.type === "single") {
              const isActive = location.pathname === group.path;
              return (
                <Link
                  key={group.path}
                  to={group.path}
                  onClick={handleLinkClick}
                  className={`block px-3 py-2 rounded-md transition-colors text-sm font-bold mb-2 ${
                    isActive ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  {group.title}
                </Link>
              );
            } else {
              // Accordion Group
              const isOpen = openGroups[group.id];
              return (
                <div key={group.id} className="mb-2">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <span>{group.title}</span>
                    {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </button>
                  
                  {isOpen && (
                    <div className="mt-1 ml-2 space-y-1 border-l-2 border-gray-100 pl-2">
                      {group.items.map(item => {
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={handleLinkClick}
                            className={`block px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                              isActive ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            }`}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
          })}

          {/* 管理者メニュー (アコーディオン化) */}
          {visibleAdminItems.length > 0 && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={() => toggleGroup("admin_section")}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider hover:bg-gray-50 rounded-md"
              >
                <span>管理者メニュー</span>
                {openGroups["admin_section"] ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </button>
              
              {openGroups["admin_section"] && (
                <div className="mt-1 ml-2 space-y-1 border-l-2 border-gray-100 pl-2">
                  {visibleAdminItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleLinkClick}
                        className={`block px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                          isActive ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-gray-200 space-y-4">
          {!isBookingManager && UIUX_SELECTOR_ROLES.has(user.role) && (
            <UiSurfaceSelector
              authenticatedUser={user}
              token={getToken()}
              currentSurface="legacy"
              expectedReleaseId={UIUX_THREE_TENANT_PILOT_RELEASE_ID}
              className="legacy-ui-surface-selector"
            />
          )}

          {user.role === "admin" && !user.oem_id && (
            <>
              <SystemStatusMonitor
                activeJobCount={activeJobCount}
                isMaintenanceMode={isMaintenanceMode}
                serverStatus={serverStatus}
              />
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">運用管理</h4>
                <button
                  onClick={handleMaintenanceToggle}
                  className={`w-full text-white font-bold py-2 px-4 rounded transition-colors ${
                    isMaintenanceMode ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
                  }`}
                >
                  {isMaintenanceMode ? "メンテナンス終了" : "メンテナンス開始"}
                </button>
              </div>
            </>
          )}

          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md"
            >
              {isEnglishBeta ? "Log out" : "🔓 ログアウト"}
            </button>
          </div>
        </div>
      </div>
      )}

      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      )}

      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        {/* 🚀 修正：設定画面にいる時は、警告バナーを出さない
            2026-08-19 修正: 以前は activeJobs.length === 0 も条件に入っていて、
            送信ジョブが1本でも走っていると支払い警告バナーごと消えていた。
            ツールを一番よく使っているテナントほど、支払い失敗に気づけない状態
            だったので、ジョブの有無で支払い警告を隠すのをやめる。 */}
        {!isBookingManager && location.pathname !== '/settings' && !user?.oem_id && userPrivileges?.role !== 'admin' && (
          <div className="relative z-50">
            {/* ケース1: 決済未完了（incomplete） */}
            {userPrivileges?.subscription_status === 'incomplete' && (
              <div className="bg-orange-600 text-white px-4 sm:px-6 py-3 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center shadow-lg border-b border-orange-700">
                <div className="flex items-start sm:items-center gap-3 text-sm font-bold">
                  <span>⚠️</span> 
                  <span>{isEnglishBeta ? "Checkout is not complete. Please confirm your payment method." : "決済手続きが完了していません。お支払い方法を確認してください。"}</span>
                </div>
                <Link to="/settings" className="self-start sm:self-auto bg-white text-orange-600 px-4 py-1.5 rounded-full text-xs font-black hover:bg-gray-100 transition-all shadow-sm shrink-0 sm:ml-4">
                  {isEnglishBeta ? "Open account settings" : "契約管理ページへ"}
                </Link>
              </div>
            )}

            {/* ケース2: 支払い失敗・遅延（past_due） */}
            {userPrivileges?.subscription_status === 'past_due' && (
              <div className="bg-red-700 text-white px-4 sm:px-6 py-3 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center shadow-lg border-b-2 border-red-900">
                <div className="flex items-start sm:items-center gap-3 text-sm font-bold animate-pulse">
                  <span>🚨</span> 
                  <span>{isEnglishBeta ? "Payment confirmation is required to keep execution available." : "【重要】お支払いの確認が取れません。このままですとサービスが停止されます。"}</span>
                </div>
                <div className="self-start sm:self-auto flex flex-wrap gap-2 shrink-0 sm:ml-4">
                  {/* 未払いの請求があるときは、その場で払い直せるStripeの
                      支払いページを最優先で出す (2026-08-19)。カード情報の
                      更新だけでは、失敗済みの請求はそのまま残る。 */}
                  {openInvoice?.hostedInvoiceUrl && (
                    <a
                      href={openInvoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white text-red-700 px-4 py-1.5 rounded-full text-xs font-black hover:bg-gray-100 transition-all shadow-sm"
                    >
                      {isEnglishBeta ? "Pay now" : "いますぐお支払い"}
                    </a>
                  )}
                  <Link to="/settings" className="bg-white/20 text-white border border-white/60 px-4 py-1.5 rounded-full text-xs font-black hover:bg-white/30 transition-all shadow-sm">
                    {isEnglishBeta ? "Update payment method" : "カード情報を更新"}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
        {!isBookingManager && !isEnglishBeta && metaStatus.fb_page_id && metaStatus.daysRemaining !== null && (
          <div className="relative z-50">
            {/* 期限切れ (赤) */}
            {metaStatus.daysRemaining <= 0 ? (
              <div className="bg-red-600 text-white px-4 sm:px-6 py-3 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center shadow-lg">
                <div className="flex items-start sm:items-center gap-3 text-sm font-bold">
                  <span>⚠️</span> 
                  <span>Facebook連携の期限が切れました。インスタントフォームの自動取り込みが停止しています。</span>
                </div>
                <Link to="/settings/marketing" className="self-start sm:self-auto bg-white text-red-600 px-4 py-1.5 rounded-full text-xs font-black hover:bg-gray-100 transition-all shadow-sm shrink-0 sm:ml-4">
                  今すぐ再連携
                </Link>
              </div>
            ) 
            /* 期限間近 10日以内 (黄色) */
            : metaStatus.daysRemaining <= 10 ? (
              <div className="bg-amber-500 text-white px-4 sm:px-6 py-2 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center shadow-md">
                <div className="flex items-start sm:items-center gap-3 text-sm font-bold">
                  <span>⚠️</span> 
                  <span>Facebook連携の期限が残り {metaStatus.daysRemaining} 日です。更新をお願いします。</span>
                </div>
                <Link to="/settings/marketing" className="self-start sm:self-auto underline text-xs font-black hover:text-amber-50 transition-all shrink-0 sm:ml-4">
                  更新設定へ
                </Link>
              </div>
            ) : null}
          </div>
        )}
        {!isEmbeddedAdminView && (
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <BrandingLogo />
          <button onClick={() => setIsMenuOpen(true)} className="text-gray-600 hover:text-gray-800 p-2 -mr-2">
            <MenuIcon />
          </button>
        </header>
        )}

        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="w-full max-w-screen-2xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
            {/* 🚀 修正：OEMユーザーが禁止パスにいる場合は、中身（Outlet）を表示せず空にする */}
            {user?.oem_id && isOemRestrictedPath(location.pathname) ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-indigo-500" size={40} />
              </div>
            ) : (
              <Outlet context={{ user, setActiveJob: addActiveJob, activeJobs, clientId: selectedClientId, isMaintenanceMode, userPrivileges, oemBranding }} />
            )}
          </div>
        </main>
      </div>
      
      {!isBookingManager && !isEmbeddedAdminView ? (
        <GlobalJobNotifier jobs={activeJobs} onCancel={handleCancelJob} onClearFinished={handleClearFinishedJobs} latestLog={latestLog} />
      ) : null}
    </div>
  )
}

export default AppLayout
